import React, { useState, useEffect, useRef, useMemo } from 'react';
import { API_BASE_URL } from '@/config/api';
import { useNavigate } from 'react-router-dom';
import Pagination from './Pagination';
import ApplicationList from '../Applications/ApplicationList';
import JobDescriptionView from './JobDescriptionView';
import BroadcastDialog from '../Broadcast/BroadcastDialog';
import JobsTable from '../../Components/JobsTable';
import useJob from '../../Hooks/jobHook';
import { JobListSkeleton } from '../Overview/SkeletonLoader';
import { Stack } from '@/Components/ui/layout';
import { Inline } from '@/Components/ui/layout';
import { Button } from '@/Components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/Components/ui/select';
import { Input } from '@/Components/ui/input';
import { Badge } from '@/Components/ui/badge';
import { Switch } from '@/Components/ui/switch';
import { Card, CardContent } from '@/Components/ui/card';
import { cn } from '@/lib/utils';
import {
  ChevronLeft,
  MoreVertical,
  X,
  Search,
  Globe,
  Lock,
  AlertTriangle,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';
// AI Assistant — publish the job list + "open job" handler to the dock.
import { useAssistantPage } from '../Applications/copilot/useAssistantPage';
import { useCompanyAccess } from '@/HireWithUs/Context/CompanyAccessContext';

const jobMatchesSearch = (job, query) => {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return true;

  return [
    job.jobTitle,
    job.job_title,
    job.id,
    job.job_id,
    job.jobType,
    job.job_type,
    job.location,
    job.companyLocation,
    job.requirement_status,
    job.requirementStatus,
  ].some((value) => String(value || '').toLowerCase().includes(normalizedQuery));
};

/** Normalize employment labels for matching only (stored data unchanged). */
const normalizeFilterToken = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '');

const EMPLOYMENT_TYPE_OPTIONS = [
  { value: 'permanent', label: 'Permanent' },
  { value: 'fulltime', label: 'Full-time' },
  { value: 'parttime', label: 'Part-time' },
  { value: 'contract', label: 'Contract' },
  { value: 'internship', label: 'Internship' },
  { value: 'temporary', label: 'Temporary' },
];

const getEmploymentTypeToken = (job) =>
  normalizeFilterToken(job.employment_type || job.employmentType || '');

/** Permanent and Full-time are treated as matching aliases in filter selection. */
const employmentTypeMatches = (job, selected) => {
  const token = getEmploymentTypeToken(job);
  if (!token) return false;
  if (selected === 'permanent') return token === 'permanent' || token === 'fulltime';
  if (selected === 'fulltime') return token === 'fulltime' || token === 'permanent';
  return token === selected;
};

const parseJobExperienceBounds = (job) => {
  const rawMin = job.min_exp ?? job.minExp ?? job.minExperience ?? job.min_experience;
  const rawMax = job.max_exp ?? job.maxExp ?? job.maxExperience ?? job.max_experience;

  const minExp =
    rawMin === null || rawMin === undefined || rawMin === '' ? null : Number(rawMin);
  const maxExp =
    rawMax === null || rawMax === undefined || rawMax === '' ? null : Number(rawMax);

  const hasMin = Number.isFinite(minExp);
  const hasMax = Number.isFinite(maxExp);
  if (!hasMin && !hasMax) return null;

  return {
    min: hasMin ? minExp : maxExp,
    max: hasMax ? maxExp : minExp,
  };
};

const EXPERIENCE_BUCKETS = {
  '0-2': { min: 0, max: 2 },
  '3-5': { min: 3, max: 5 },
  '6-10': { min: 6, max: 10 },
  '10+': { min: 10, max: Number.POSITIVE_INFINITY },
};

const experienceRangesOverlap = (jobBounds, selected) => {
  if (!jobBounds || !selected) return false;
  return jobBounds.max >= selected.min && jobBounds.min <= selected.max;
};

function JobList({ selectedJobIdFromUrl, highlightedApplicationId }) {
  const navigate = useNavigate();
  const { can } = useCompanyAccess();
  const { jobs, loading, refreshJobs, togglePinJob, deleteJob } = useJob();

  const [currentPage, setCurrentPage] = useState(1);
  const selectedJobId = selectedJobIdFromUrl || null;
  const [viewingJobDescription, setViewingJobDescription] = useState(null);
  // Job whose Broadcast dialog is open (null = closed).
  const [broadcastJobId, setBroadcastJobId] = useState(null);
  const JobsPerPage = 10;

  const [showThreeDotMenu, setShowThreeDotMenu] = useState(false);
  const menuRef = useRef(null);
  const candidateSearchRef = useRef(null);
  const [jobSearchQuery, setJobSearchQuery] = useState('');
  const [candidateSearchQuery, setCandidateSearchQuery] = useState('');
  const [isCandidateSearchOpen, setIsCandidateSearchOpen] = useState(false);
  const [candidateResults, setCandidateResults] = useState([]);
  const [isSearchingCandidates, setIsSearchingCandidates] = useState(false);
  const [candidateSearchError, setCandidateSearchError] = useState('');

  // Debug log removed — was dumping every job (titles, IDs, internal state)
  // to console on every render.

  // Filter states — same layout as before; only Type → Employment Type
  const [filters, setFilters] = useState({
    status: 'all',
    employmentType: 'all',
    location: '',
    experienceRange: 'all',
    datePosted: 'all',
  });

  // ── Public-board visibility ────────────────────────────────────────────────
  // Two-tier model:
  //   • `companyVisibility.is_public` = master switch (toggle at the top)
  //   • `jobVisibilityMap[jobId]`     = per-job eye toggle in the table
  //
  // We hydrate both on mount via two parallel calls. The visibility CARD
  // ALWAYS renders — even on error — so the employer sees the feature
  // exists and gets a clear error message instead of silent absence.
  //
  // States:
  //   • visibilityState === 'loading' → show skeleton in the card
  //   • visibilityState === 'ready'   → working switch + eyes
  //   • visibilityState === 'error'   → card with red banner + error message
  const [companyVisibility, setCompanyVisibility] = useState(null);
  const [jobVisibilityMap, setJobVisibilityMap] = useState({});
  const [visibilityState, setVisibilityState] = useState('loading');
  const [visibilityError, setVisibilityError] = useState(null);

  // Match the rest of the app: read the logged-in employer's email from
  // localStorage and pass it as `?company_email=...`. This is the same
  // convention used by /api/get_jd, /api/overview_stats, and the
  // visibility endpoints fall back to session if it's present anyway.
  const employerEmail =
    typeof window !== 'undefined'
      ? sessionStorage.getItem('employer_email') ||
        localStorage.getItem('employer_email') ||
        ''
      : '';

  // Pick the API host based on where we're running. In dev the React
  // dev server (port 3000) and Flask (port 5000) live on different
  // origins, so we need an absolute URL; in production they share the
  // same domain (Flask serves the static build) so a relative URL
  // routes through Cloudflare → Flask without any cross-origin dance.
  // Centralized API base (handles localhost/staging/prod + REACT_APP_API_URL).
  const API_BASE = API_BASE_URL;

  const refreshVisibility = async () => {
    if (!employerEmail) {
      // No email cached — usually means the employer isn't logged in yet.
      // Render the card in "error" state with a helpful message.
      setVisibilityError('Not logged in as an employer');
      setVisibilityState('error');
      return;
    }

    setVisibilityState('loading');
    setVisibilityError(null);
    try {
      const qs = `?company_email=${encodeURIComponent(employerEmail)}`;
      const [empRes, jobsRes] = await Promise.all([
        fetch(`${API_BASE}/api/employer/visibility${qs}`, {
          credentials: 'include',
        }),
        fetch(`${API_BASE}/api/employer/jobs/visibility${qs}`, {
          credentials: 'include',
        }),
      ]);

      // The master-switch endpoint is the critical one — without it we
      // can't render anything sensible. Surface a clear error instead
      // of silently hiding the UI.
      if (!empRes.ok) {
        let msg = `HTTP ${empRes.status}`;
        try {
          const body = await empRes.json();
          msg = body.error || msg;
        } catch (_) {
          // body wasn't JSON — keep the status code as the message
        }
        console.error('[visibility] /api/employer/visibility failed:', msg);
        setVisibilityError(msg);
        setVisibilityState('error');
        return;
      }

      const empData = await empRes.json();
      // Debug log removed — was leaking visibility master config to console.
      setCompanyVisibility(empData);

      if (jobsRes.ok) {
        const data = await jobsRes.json();
        // Debug log removed — was leaking the full jobs-visibility list.
        const map = {};
        for (const j of data.jobs || []) map[j.job_id] = Boolean(j.is_public);
        setJobVisibilityMap(map);
      }
      setVisibilityState('ready');
    } catch (e) {
      console.error('[visibility] network error:', e);
      setVisibilityError(e.message || 'Network error');
      setVisibilityState('error');
    }
  };

  useEffect(() => {
    refreshVisibility();
  }, []);

  const handleToggleCompanyVisibility = async (next) => {
    const previous = Boolean(companyVisibility?.is_public);
    // Optimistic flip
    setCompanyVisibility((c) => ({ ...(c || {}), is_public: next }));
    try {
      const res = await fetch(`${API_BASE}/api/employer/visibility`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_email: employerEmail,
          is_public: next,
        }),
      });
      if (!res.ok) throw new Error('Failed to update');
      toast.success(
        next
          ? 'Your company is now visible on the public job board.'
          : 'Your company is hidden from the public job board.',
      );
    } catch (e) {
      // Roll back the optimistic UI flip
      setCompanyVisibility((c) => ({ ...(c || {}), is_public: previous }));
      toast.error('Could not update visibility. Try again.');
    }
  };

  const handleToggleJobVisibility = async (jobId, next) => {
    const previous = Boolean(jobVisibilityMap[jobId]);
    setJobVisibilityMap((m) => ({ ...m, [jobId]: next }));
    try {
      const res = await fetch(
        `${API_BASE}/api/jd/${encodeURIComponent(jobId)}/visibility`,
        {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            company_email: employerEmail,
            is_public: next,
          }),
        },
      );
      if (!res.ok) throw new Error('Failed to update');
      // Bump the master-level public_job_count so the warning banner
      // updates without a full refetch.
      setCompanyVisibility((c) => {
        if (!c) return c;
        const delta = next ? 1 : -1;
        return {
          ...c,
          public_job_count: Math.max(0, (c.public_job_count || 0) + delta),
        };
      });
    } catch (e) {
      setJobVisibilityMap((m) => ({ ...m, [jobId]: previous }));
      toast.error('Could not update job visibility. Try again.');
    }
  };

  // The Broadcast dialog can publish a job (and switch the company careers
  // page on). Mirror what it reports so the eye toggles and the master
  // switch on this page never show a stale state.
  const handleBroadcastStateChange = (state) => {
    const job = state?.job;
    if (!job) return;
    const wasPublic = Boolean(jobVisibilityMap[job.job_id]);
    setJobVisibilityMap((m) => ({ ...m, [job.job_id]: Boolean(job.is_public) }));
    setCompanyVisibility((c) => {
      if (!c) return c;
      const delta = Number(Boolean(job.is_public)) - Number(wasPublic);
      return {
        ...c,
        is_public: Boolean(job.company_public),
        public_job_count: Math.max(0, (c.public_job_count || 0) + delta),
      };
    });
  };

  // Show the inline warning when the company master switch is OFF but
  // the employer has flipped the per-job eye on for at least one job.
  // That state means the candidate-facing board WON'T show those jobs
  // until the master switch is also turned on.
  const visibilityWarning = useMemo(() => {
    if (!companyVisibility) return null;
    const masterOff = !companyVisibility.is_public;
    const hasEnabledJobs = (companyVisibility.public_job_count || 0) > 0;
    return masterOff && hasEnabledJobs ? companyVisibility.public_job_count : null;
  }, [companyVisibility]);
  // ──────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowThreeDotMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        candidateSearchRef.current &&
        !candidateSearchRef.current.contains(event.target)
      ) {
        setIsCandidateSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Jobs are already loaded for this page, so their matches are immediate.
  // Candidate matches are company-wide and intentionally fetched only after
  // a short debounce; fetching every job's applicant list here would not
  // scale as a company grows.
  useEffect(() => {
    const query = candidateSearchQuery.trim();
    if (query.length < 2) {
      setCandidateResults([]);
      setCandidateSearchError('');
      setIsSearchingCandidates(false);
      return undefined;
    }

    const controller = new AbortController();
    setIsSearchingCandidates(true);
    setCandidateSearchError('');
    const timeoutId = window.setTimeout(async () => {
      try {
        const response = await fetch(
          `${API_BASE}/api/employer/candidate-search?q=${encodeURIComponent(query)}`,
          { credentials: 'include', signal: controller.signal },
        );
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(payload.error || 'Candidate search is unavailable.');
        }
        setCandidateResults(Array.isArray(payload.candidates) ? payload.candidates : []);
      } catch (error) {
        if (error.name !== 'AbortError') {
          setCandidateResults([]);
          setCandidateSearchError('Could not search candidates. Please try again.');
        }
      } finally {
        if (!controller.signal.aborted) setIsSearchingCandidates(false);
      }
    }, 300);

    return () => {
      window.clearTimeout(timeoutId);
      controller.abort();
    };
  }, [candidateSearchQuery, API_BASE]);

  // Applicant search can narrow the jobs table to related job IDs.
  const applicantMatchedJobIds = useMemo(() => {
    const query = candidateSearchQuery.trim();
    if (query.length < 2) return null;
    if (isSearchingCandidates) return null;
    return new Set(
      (candidateResults || [])
        .map((candidate) => String(candidate.job_id || ''))
        .filter(Boolean),
    );
  }, [candidateSearchQuery, candidateResults, isSearchingCandidates]);

  // Filter jobs based on selected filters
  const filteredJobs = useMemo(() => {
    return jobs.filter((job) => {
      if (!jobMatchesSearch(job, jobSearchQuery)) return false;

      // When candidate search is active, keep only jobs from candidate-search hits.
      if (applicantMatchedJobIds) {
        const jobId = String(job.job_id || job.id || '');
        if (!applicantMatchedJobIds.has(jobId)) return false;
      }

      // Status filter (same Active / Inactive behavior as before)
      if (filters.status !== 'all') {
        const requirementStatus = String(
          job.requirement_status || job.requirementStatus || '',
        )
          .trim()
          .toLowerCase();
        const jobStatus = ['', 'active', 'open'].includes(requirementStatus)
          ? 'active'
          : 'inactive';
        if (jobStatus !== filters.status) return false;
      }

      // Employment Type — replaces previous All Types / job_type filter
      if (filters.employmentType !== 'all') {
        if (!employmentTypeMatches(job, filters.employmentType)) return false;
      }

      // Location filter (case-insensitive partial match)
      if (filters.location) {
        const jobLocation = (job.location || job.companyLocation || '').toLowerCase();
        if (!jobLocation.includes(filters.location.toLowerCase())) return false;
      }

      // Experience — min_exp / max_exp with overlap
      if (filters.experienceRange !== 'all') {
        const jobBounds = parseJobExperienceBounds(job);
        const selected = EXPERIENCE_BUCKETS[filters.experienceRange];
        if (!jobBounds || !selected || !experienceRangesOverlap(jobBounds, selected)) {
          return false;
        }
      }

      // Date Posted filter
      if (filters.datePosted !== 'all' && job.created_at) {
        const createdDate = new Date(job.created_at);
        const today = new Date();
        const diffTime = Math.abs(today - createdDate);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        switch (filters.datePosted) {
          case 'today':
            if (diffDays > 1) return false;
            break;
          case '7days':
            if (diffDays > 7) return false;
            break;
          case '30days':
            if (diffDays > 30) return false;
            break;
          default:
            break;
        }
      }

      return true;
    });
  }, [jobs, filters, jobSearchQuery, applicantMatchedJobIds]);

  const indexOfLastJob = currentPage * JobsPerPage;
  const indexOfFirstJob = indexOfLastJob - JobsPerPage;
  const currentJobs = filteredJobs.slice(indexOfFirstJob, indexOfLastJob);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleViewApplications = (jobId) => {
    navigate(`/hirewithus/my-jobs/${jobId}`);
  };

  // AI Assistant page adapter: the job list on screen and how to open one.
  // With a job selected the embedded ApplicationList owns the page (it publishes
  // the applicants the action cards need), so this adapter steps aside (null).
  useAssistantPage(selectedJobId ? null : { page: 'my-jobs', jobs, onOpenJob: handleViewApplications });

  const handleCandidateResultClick = (candidate) => {
    const applicationId = candidate.application_id || candidate.id;
    const query = applicationId ? `?applicationId=${encodeURIComponent(applicationId)}` : '';
    setCandidateSearchQuery('');
    setIsCandidateSearchOpen(false);
    navigate(`/hirewithus/my-jobs/${encodeURIComponent(candidate.job_id)}${query}`);
  };

  const handleCloseApplications = () => {
    navigate('/hirewithus/my-jobs');
    refreshJobs();
  };

  const handleViewJobDescription = (jobId) => {
    const job = jobs.find(j => (j.job_id || j.id) === jobId);
    setViewingJobDescription(job);
  };

  const handleCloseJobDescription = () => {
    setViewingJobDescription(null);
  };

  const handleSaveJobDescription = (updatedJob) => {
    refreshJobs();
    setViewingJobDescription(null);
  };

  const handlePinJob = async (jobId) => {
    try {
      await togglePinJob(jobId);
    } catch (e) {
      console.error("Failed to pin/unpin job:", e);
      window.alert("Failed to pin/unpin job. Please try again.");
    }
  };

  const handleDeleteJob = async (jobId) => {
    const ok = window.confirm("Are you sure you want to delete this job?");
    if (!ok) return;

    try {
      await deleteJob(jobId);
    } catch (e) {
      console.error("Failed to delete job:", e);
      window.alert("Failed to delete job. Please try again.");
    }
  };

  useEffect(() => {
    const handleResumeUpload = () => {
      refreshJobs();
    };

    window.addEventListener('refreshJobs', handleResumeUpload);
    
    return () => {
      window.removeEventListener('refreshJobs', handleResumeUpload);
    };
  }, [refreshJobs]);

  // Reset to page 1 when filters or search change.
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, jobSearchQuery, candidateSearchQuery, candidateResults]);

  const totalPages = Math.ceil(filteredJobs.length / JobsPerPage);

  // Filter handlers
  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({ ...prev, [filterType]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      status: 'all',
      employmentType: 'all',
      location: '',
      experienceRange: 'all',
      datePosted: 'all',
    });
  };

  const hasActiveFilters = useMemo(() => {
    return filters.status !== 'all' ||
           filters.employmentType !== 'all' ||
           filters.location !== '' ||
           filters.experienceRange !== 'all' ||
           filters.datePosted !== 'all';
  }, [filters]);

  // Get active filters for display
  const activeFiltersList = useMemo(() => {
    const active = [];

    if (filters.status !== 'all') {
      active.push({
        key: 'status',
        label: `Status: ${filters.status}`,
        value: filters.status,
      });
    }

    if (filters.employmentType !== 'all') {
      const employmentLabel =
        EMPLOYMENT_TYPE_OPTIONS.find((option) => option.value === filters.employmentType)?.label ||
        filters.employmentType;
      active.push({
        key: 'employmentType',
        label: `Employment: ${employmentLabel}`,
        value: filters.employmentType,
      });
    }

    if (filters.location) {
      active.push({
        key: 'location',
        label: `Location: ${filters.location}`,
        value: filters.location,
      });
    }

    if (filters.experienceRange !== 'all') {
      active.push({
        key: 'experienceRange',
        label: `Experience: ${filters.experienceRange} years`,
        value: filters.experienceRange,
      });
    }

    if (filters.datePosted !== 'all') {
      const dateLabels = {
        today: 'Today',
        '7days': 'Last 7 days',
        '30days': 'Last 30 days',
      };
      active.push({
        key: 'datePosted',
        label: `Posted: ${dateLabels[filters.datePosted] || filters.datePosted}`,
        value: filters.datePosted,
      });
    }

    return active;
  }, [filters]);

  const handleRemoveFilter = (filterKey) => {
    if (filterKey === 'location') {
      handleFilterChange(filterKey, '');
    } else {
      handleFilterChange(filterKey, 'all');
    }
  };

  if (loading && jobs.length === 0 && !selectedJobId) {
    return <JobListSkeleton />;
  }

  return (
    <Stack spacing="md">
      {viewingJobDescription ? (
        <JobDescriptionView
          job={viewingJobDescription}
          onClose={handleCloseJobDescription}
          onSave={handleSaveJobDescription}
        />
      ) : selectedJobId ? (
        <Stack spacing="md">
          <Inline justify="between" align="center">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleCloseApplications}
              title="Back to Jobs"
              className="w-9 h-9"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            {can('settings.vendors') && <div className="relative" ref={menuRef}>
              <Button
                variant="ghost"
                size="icon"
                title="More options"
                onClick={() => setShowThreeDotMenu(!showThreeDotMenu)}
                className="w-9 h-9"
              >
                <MoreVertical className="h-5 w-5" />
              </Button>
              {showThreeDotMenu && (
                <div className={cn(
                  "absolute top-full right-0 mt-1 bg-white border border-border rounded-lg shadow-lg",
                  "min-w-[180px] z-[1000] overflow-hidden animate-in fade-in-0 zoom-in-95"
                )}>
                  <button
                    className={cn(
                      "flex items-center gap-2.5 w-full px-4 py-3 bg-transparent border-none",
                      "text-foreground text-sm font-medium cursor-pointer transition-colors",
                      "hover:bg-accent text-left"
                    )}
                    onClick={() => {
                      setShowThreeDotMenu(false);
                      navigate(
                        `/hirewithus/settings/recruiters${
                          selectedJobId ? `?jobId=${encodeURIComponent(selectedJobId)}` : ''
                        }`
                      );
                    }}
                  >
                    <Users className="h-4 w-4" />
                    Manage recruiters
                  </button>
                </div>
              )}
            </div>}
          </Inline>

          <ApplicationList
            jobId={selectedJobId}
            highlightedApplicationId={highlightedApplicationId}
          />
        </Stack>
      ) : (
        <Stack spacing="md">
          {/* Header with Title */}
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-semibold text-foreground">
              All Jobs ({filteredJobs.length}{filteredJobs.length !== jobs.length ? ` of ${jobs.length}` : ''})
            </h2>
          </div>

          <div className="flex w-full flex-col gap-3 sm:flex-row">
            <div className="relative w-full max-w-2xl">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="text"
                value={jobSearchQuery}
                onChange={(event) => setJobSearchQuery(event.target.value)}
                placeholder="Search job posts..."
                aria-label="Search job posts"
                className="pl-10 pr-10"
              />
              {jobSearchQuery && (
                <Button
                  variant="ghost"
                  size="icon"
                  type="button"
                  onClick={() => setJobSearchQuery('')}
                  className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2"
                  aria-label="Clear job search"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>

            <div className="relative w-full max-w-2xl" ref={candidateSearchRef}>
              <Search className="absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="text"
                value={candidateSearchQuery}
                onChange={(event) => {
                  setCandidateSearchQuery(event.target.value);
                  setIsCandidateSearchOpen(true);
                }}
                onFocus={() => setIsCandidateSearchOpen(true)}
                placeholder="Search candidates..."
                aria-label="Search candidates"
                className="pl-10 pr-10"
              />
              {candidateSearchQuery && (
                <Button
                  variant="ghost"
                  size="icon"
                  type="button"
                  onClick={() => {
                    setCandidateSearchQuery('');
                    setIsCandidateSearchOpen(false);
                  }}
                  className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2"
                  aria-label="Clear candidate search"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}

              {isCandidateSearchOpen && candidateSearchQuery.trim() && (
                <div className="absolute z-50 mt-2 w-full overflow-hidden rounded-lg border border-border bg-popover shadow-lg">
                  {candidateSearchQuery.trim().length < 2 ? (
                    <p className="p-3 text-sm text-muted-foreground">
                      Keep typing to search candidates across all jobs.
                    </p>
                  ) : isSearchingCandidates ? (
                    <p className="p-3 text-sm text-muted-foreground">Searching candidates…</p>
                  ) : candidateSearchError ? (
                    <p className="p-3 text-sm text-destructive">{candidateSearchError}</p>
                  ) : candidateResults.length > 0 ? (
                    <div className="p-2">
                      <p className="px-2 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Candidates
                      </p>
                      {candidateResults.map((candidate) => (
                        <button
                          key={`${candidate.job_id}-${candidate.application_id || candidate.id}`}
                          type="button"
                          onClick={() => handleCandidateResultClick(candidate)}
                          className="flex w-full items-center justify-between gap-3 rounded-md px-2 py-2 text-left hover:bg-accent"
                        >
                          <span className="min-w-0">
                            <span className="block truncate text-sm font-medium text-foreground">
                              {candidate.name || 'Unnamed candidate'}
                            </span>
                            <span className="block truncate text-xs text-muted-foreground">
                              {candidate.email || candidate.candidate_email}
                            </span>
                          </span>
                          <span className="shrink-0 text-right text-xs text-muted-foreground">
                            <span className="block max-w-[170px] truncate">{candidate.job_title}</span>
                            <span className="capitalize">{candidate.status || 'Applied'}</span>
                          </span>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="p-3 text-sm text-muted-foreground">No candidates found.</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Filter Section */}
          <div className="flex items-start gap-3">
            <span className="mt-2 text-sm font-semibold text-foreground whitespace-nowrap">Filters</span>

            <div className="flex flex-wrap items-center gap-3 flex-1">
              {/* Status Filter */}
              <Select
                value={filters.status}
                onValueChange={(value) => handleFilterChange('status', value)}
              >
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>

              {/* Employment Type — replaces previous All Types */}
              <Select
                value={filters.employmentType}
                onValueChange={(value) => handleFilterChange('employmentType', value)}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Employment Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Employment Type</SelectItem>
                  {EMPLOYMENT_TYPE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Location Filter */}
              <div className="relative w-[160px]">
                <Input
                  type="text"
                  placeholder="Location..."
                  value={filters.location}
                  onChange={(e) => handleFilterChange('location', e.target.value)}
                  className="w-full pr-8"
                />
                {filters.location && (
                  <button
                    type="button"
                    onClick={() => handleFilterChange('location', '')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>

              {/* Experience Range Filter */}
              <Select
                value={filters.experienceRange}
                onValueChange={(value) => handleFilterChange('experienceRange', value)}
              >
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Experience" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Experience</SelectItem>
                  <SelectItem value="0-2">0-2 years</SelectItem>
                  <SelectItem value="3-5">3-5 years</SelectItem>
                  <SelectItem value="6-10">6-10 years</SelectItem>
                  <SelectItem value="10+">10+ years</SelectItem>
                </SelectContent>
              </Select>

              {/* Date Posted Filter */}
              <Select
                value={filters.datePosted}
                onValueChange={(value) => handleFilterChange('datePosted', value)}
              >
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Date Posted" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Date Posted</SelectItem>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="7days">Last 7 days</SelectItem>
                  <SelectItem value="30days">Last 30 days</SelectItem>
                </SelectContent>
              </Select>

              {/* Clear Filters Button - Fixed width to prevent layout shift */}
              <div className="w-[90px]">
                {hasActiveFilters && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleClearFilters}
                    className="gap-2 w-full"
                  >
                    <X className="h-4 w-4" />
                    Clear
                  </Button>
                )}
              </div>
            </div>

            {/* Public-board listing — compact toggle pinned to the top-right. It
                covers the whole company, so only people who manage company settings get it. */}
            {can('settings.company') && <div
              className={cn(
                'flex shrink-0 items-center gap-2 self-start rounded-lg border px-3 py-1.5',
                visibilityState === 'error'
                  ? 'border-destructive/40'
                  : companyVisibility?.is_public
                  ? 'border-emerald-300 bg-emerald-50/50'
                  : 'border-border/60',
              )}
              title={
                visibilityState === 'loading'
                  ? 'Loading visibility settings…'
                  : visibilityState === 'error'
                  ? `Could not load visibility settings: ${visibilityError}.`
                  : companyVisibility?.is_public
                  ? 'Your company is visible on the public job board. Use the eye icon on each row to control individual jobs.'
                  : 'When off, none of your jobs appear publicly — even ones you mark visible per row.'
              }
            >
              {visibilityState === 'error' ? (
                <AlertTriangle className="h-4 w-4 text-destructive" />
              ) : companyVisibility?.is_public ? (
                <Globe className="h-4 w-4 text-emerald-600" />
              ) : (
                <Lock className="h-4 w-4 text-muted-foreground" />
              )}
              <span className="whitespace-nowrap text-sm font-medium text-foreground">
                List publicly
              </span>
              <Switch
                checked={Boolean(companyVisibility?.is_public)}
                onCheckedChange={handleToggleCompanyVisibility}
                disabled={visibilityState !== 'ready'}
                aria-label="Toggle public job board listing"
              />
            </div>}
          </div>

          {/* Active Filters Display */}
          {activeFiltersList.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-muted-foreground">Active filters:</span>
              {activeFiltersList.map((filter) => (
                <Badge 
                  key={filter.key} 
                  variant="secondary"
                  className="gap-1.5 pr-1 pl-3 py-1"
                >
                  <span>{filter.label}</span>
                  <button
                    onClick={() => handleRemoveFilter(filter.key)}
                    className="ml-1 hover:bg-muted rounded-sm p-0.5 transition-colors"
                    aria-label={`Remove ${filter.label} filter`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          )}

          {/* Inline warning: master OFF but per-job eyes are ON. Only one
              warning at a time to avoid noise. */}
          {visibilityWarning && (
            <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700/40 dark:bg-amber-950/40 dark:text-amber-200">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <div>
                <p className="font-medium">
                  {visibilityWarning} job{visibilityWarning === 1 ? '' : 's'}{' '}
                  marked public, but your company's master switch is off.
                </p>
                <p className="text-xs">
                  Turn on the switch above for those jobs to appear on the
                  public job board.
                </p>
              </div>
            </div>
          )}

          <JobsTable
            jobs={currentJobs}
            loading={loading && jobs.length === 0}
            error={null}
            title={null}
            handleViewApplications={handleViewApplications}
            onViewJobDescription={handleViewJobDescription}
            onPinJob={can('jobs.edit') ? handlePinJob : undefined}
            onDeleteJob={can('jobs.close') ? handleDeleteJob : undefined}
            onBroadcastJob={can('jobs.broadcast') ? setBroadcastJobId : undefined}
            // Public-board visibility — passed unconditionally so the eye
            // toggle always shows. Click handlers no-op gracefully if the
            // visibility data hasn't loaded yet (PATCH still works).
            visibilityMap={jobVisibilityMap}
            onToggleVisibility={can('jobs.broadcast') ? handleToggleJobVisibility : undefined}
            companyIsPublic={Boolean(companyVisibility?.is_public)}
            loadingComponent={<JobListSkeleton />}
          />

          {filteredJobs.length > JobsPerPage && (
            <div className="mt-4">
              <Pagination
                totalPages={totalPages}
                currentPage={currentPage}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </Stack>
      )}

      <BroadcastDialog
        jobId={broadcastJobId}
        open={Boolean(broadcastJobId)}
        onOpenChange={(open) => {
          if (!open) setBroadcastJobId(null);
        }}
        onStateChange={handleBroadcastStateChange}
      />
    </Stack>
  );
}

export default JobList;
